/**
 * Wizard de Cliente con TanStack Form
 * Reemplaza ClienteFormWizard.tsx con validaciones declarativas
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useStore } from '@tanstack/react-store'
import { useClienteForm } from './useClienteForm'
import { WizardStepper } from './WizardStepper'
import { WizardNavigation } from './WizardNavigation'
import { StepIdentidadNew } from './steps/StepIdentidadNew'
import { StepDomicilioNew } from './steps/StepDomicilioNew'
import { StepFiscalNew } from './steps/StepFiscalNew'
import { StepServiciosNew } from './steps/StepServiciosNew'
import { StepDeudaInicialNew } from './steps/StepDeudaInicialNew'
import { useUsuariosOrg } from '@/hooks/useUsuarios'
import { useServicios, useServiciosDelCliente } from '@/hooks/useServicios'
import { usePermissions } from '@/hooks/usePermissions'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AlertCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ClienteConFacetaContable, Servicio, ActividadEconomica } from '@/types/database'
import type { AsignacionImpuestoCliente } from '@/services/clientesService'
import type { ServicioLeido } from '@/services/serviciosClienteEdicion'
import { STEP_SCHEMAS, STEP_SCHEMAS_CON_DEUDA, type ClienteFormValues, type ImpuestoLiquidar, type ServicioConPrecio } from './clienteSchema'
import { impuestosIniciales } from './impuestosIniciales'

/**
 * Lo que el guardado de una edición necesita saber además de los valores del formulario.
 * `serviciosBase` son las filas de servicios con precio que el formulario precargó: el guardado aplica
 * la diferencia contra ellas. En null, el formulario nunca los leyó y el guardado no debe tocarlos.
 */
export interface ContextoEdicionCliente {
  serviciosBase: ServicioLeido[] | null
}

interface ClienteWizardNewProps {
  /** `impuestos_cliente` comes from `clientesService.obtenerPorId`. Without it, the tax list comes from the JSONB. */
  cliente?: (ClienteConFacetaContable & { impuestos_cliente?: AsignacionImpuestoCliente[] }) | null
  servicios?: Servicio[]
  serviciosSeleccionados?: string[]
  /** Ids del catálogo de `etiquetas_cliente` que ya lleva el cliente, en modo edición. */
  etiquetasSeleccionadas?: string[]
  /** Opt-in: solo lo pasa el caller que reconcilia las etiquetas después del submit. */
  mostrarEtiquetas?: boolean
  onSubmit: (data: ClienteFormValues, contexto: ContextoEdicionCliente) => void | Promise<void>
  onCancel?: () => void
  /** Guardado en curso: el botón lo muestra y no se acepta otro submit. */
  isSaving?: boolean
  /** Receives whether the form has unsaved changes. Reports false on unmount. */
  onDirtyChange?: (dirty: boolean) => void
  /** Valores con los que arranca el alta. En edición no se usan. */
  valoresIniciales?: Partial<ClienteFormValues>
}

// Configuración de pasos. El alta agrega «Deuda inicial»; la edición no, porque
// `actualizarCompleto` no lee `deudasIniciales`.
const WIZARD_STEPS_SIN_DEUDA = [
  { id: 'identidad', title: 'Identidad', description: 'Datos básicos' },
  { id: 'domicilio', title: 'Domicilio', description: 'Dirección fiscal' },
  { id: 'fiscal', title: 'Fiscal', description: 'Datos impositivos' },
  { id: 'servicios', title: 'Servicios', description: 'Honorarios' },
]
const WIZARD_STEPS_CON_DEUDA = [
  ...WIZARD_STEPS_SIN_DEUDA,
  { id: 'deuda-inicial', title: 'Deuda inicial', description: 'Saldo al alta' },
]

// Helper para capitalizar estado
function capitalizeEstado(estado?: string | null): 'Activo' | 'Inactivo' {
  if (!estado) return 'Activo'
  const map: Record<string, 'Activo' | 'Inactivo'> = {
    activo: 'Activo',
    inactivo: 'Inactivo',
  }
  return map[estado.toLowerCase()] || 'Activo'
}

// Helper para obtener nombre y apellido separados
function getNombreApellidoParaFormulario(
  persona: { nombre?: string | null; apellido?: string | null; nombre_completo?: string | null; razon_social?: string | null } | null,
  tipoPersona?: string
): { nombre: string; apellido: string } {
  if (!persona) return { nombre: '', apellido: '' }

  if (tipoPersona === 'juridica') {
    return { nombre: persona.razon_social || '', apellido: '' }
  }

  // Ya hay campos separados en la BD
  if (persona.nombre || persona.apellido) {
    return {
      nombre: persona.nombre || '',
      apellido: persona.apellido || ''
    }
  }

  // Fallback: intentar separar nombre_completo
  if (persona.nombre_completo) {
    if (persona.nombre_completo.includes(',')) {
      const partes = persona.nombre_completo.split(',').map(p => p.trim())
      if (partes.length === 2) {
        return { nombre: partes[1], apellido: partes[0] }
      }
    } else {
      const partes = persona.nombre_completo.trim().split(/\s+/)
      if (partes.length >= 2) {
        return { nombre: partes[0], apellido: partes.slice(1).join(' ') }
      }
      return { nombre: persona.nombre_completo, apellido: '' }
    }
  }

  return { nombre: '', apellido: '' }
}

// Las filas de servicios leídas, con la forma del campo `serviciosConPrecio` del formulario.
function serviciosDelFormulario(leidos: ServicioLeido[]): ServicioConPrecio[] {
  return leidos.map((s) => ({
    servicio_id: s.servicio_id,
    precio: s.precio,
    frecuencia: s.frecuencia as ServicioConPrecio['frecuencia'],
    mes_inicio: s.mes_inicio ?? 1,
  }))
}

// Helper para formatear CUIT a string con guiones
function formatCuitToString(cuit: number | string | null): string {
  if (!cuit) return ''
  const digits = cuit.toString().replace(/\D/g, '')
  if (digits.length !== 11) return digits
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`
}

export function ClienteWizardNew({
  cliente,
  servicios: _servicios = [],
  serviciosSeleccionados = [],
  etiquetasSeleccionadas = [],
  mostrarEtiquetas = false,
  onSubmit,
  isSaving = false,
  onDirtyChange,
  valoresIniciales,
}: ClienteWizardNewProps) {
  // _servicios se pasa para compatibilidad pero usamos serviciosDisponibles desde API
  const isEditMode = !!cliente
  const { puede } = usePermissions()

  // Cargar usuarios de la organización
  const { data: usuarios = [] } = useUsuariosOrg()

  // Cargar servicios disponibles usando hook de TanStack Query
  const { data: serviciosData = [] } = useServicios()

  // Cargar servicios del cliente en modo edición. Sin datos todavía, `serviciosLeidos` es undefined.
  const { data: serviciosLeidos } = useServiciosDelCliente(cliente?.id || '')
  const serviciosDisponibles = useMemo(
    () => serviciosData.filter((s) => s.activo && s.periodicidad !== 'unico'),
    [serviciosData]
  )

  // El paso de deuda inicial es solo del alta. `actualizarCompleto` (edición) no lee
  // `deudasIniciales`. La RPC exige crearMovimiento para cualquier deuda, así que sin ese permiso
  // no hay nada que el paso pueda guardar.
  const mostrarPasoDeuda = !isEditMode && puede.crearMovimiento
  const wizardSteps = mostrarPasoDeuda ? WIZARD_STEPS_CON_DEUDA : WIZARD_STEPS_SIN_DEUDA

  // Construir defaultValues desde cliente existente
  const defaultValues = useMemo<Partial<ClienteFormValues>>(() => {
    if (!cliente) return valoresIniciales ?? {}

    const persona = cliente.persona
    const faceta = Array.isArray(cliente.faceta_contable)
      ? cliente.faceta_contable[0]
      : cliente.faceta_contable
    const { nombre, apellido } = getNombreApellidoParaFormulario(persona ?? null, persona?.tipo ?? undefined)

    return {
      tipo_persona: (persona?.tipo as 'fisica' | 'juridica' | 'sucesion_indivisa') || 'fisica',
      nombre,
      apellido,
      nombre_fantasia: persona?.nombre_fantasia || null,
      cuit: persona?.cuit ? formatCuitToString(persona.cuit) : '',
      email: persona?.email || '',
      telefono: persona?.telefono || '',
      domicilio_calle: persona?.domicilio_calle || null,
      domicilio_numero: persona?.domicilio_numero || null,
      domicilio_piso: persona?.domicilio_piso || null,
      domicilio_depto: persona?.domicilio_depto || null,
      domicilio_cp: persona?.domicilio_cp || null,
      domicilio_localidad: persona?.domicilio_localidad || null,
      domicilio_provincia: persona?.domicilio_provincia || '',
      domicilio_barrio: persona?.domicilio_barrio || null,
      domicilio_referencia: persona?.domicilio_referencia || null,
      condicion_iva: faceta?.condicion_iva || null,
      categoria_monotributo: faceta?.categoria_monotributo || null,
      provincia: faceta?.provincia_principal || persona?.domicilio_provincia || null,
      actividad: faceta?.actividad_principal || null,
      codigo_actividad_afip: faceta?.codigo_actividad_afip || null,
      actividades_economicas: (faceta?.actividades_economicas as ActividadEconomica[]) || [],
      cliente_desde: faceta?.honorarios_desde || cliente.fecha_alta || null,
      descripcion_tarea: cliente.descripcion || null,
      cierre_ejercicio: faceta?.cierre_ejercicio || null,
      honorarios_mensuales: faceta?.honorarios_mensuales || 0,
      estado: capitalizeEstado(cliente.estado),
      // Datos de Inscripción
      inscripcion_numero: faceta?.inscripcion_numero || null,
      inscripcion_fojas: faceta?.inscripcion_fojas || null,
      inscripcion_tomo: faceta?.inscripcion_tomo || null,
      inscripcion_protocolo: faceta?.inscripcion_protocolo || null,
      inscripcion_fecha: faceta?.inscripcion_fecha || null,
      inscripcion_ano: faceta?.inscripcion_ano || null,
      responsables: cliente.responsables?.map((r: { usuario_id: string }) => r.usuario_id) || [],
      clave_fiscal: faceta?.clave_fiscal || null,
      impuestos: faceta?.impuestos_inscriptos || null,
      regimen_iibb: (faceta?.regimen_iibb as 'local' | 'convenio_multilateral' | 'simplificado' | 'no_gravado') || null,
      impuestos_liquidar: impuestosIniciales(
        cliente.impuestos_cliente,
        faceta?.impuestos_liquidar as ImpuestoLiquidar[] | null | undefined
      ),
      admin_cuit: persona?.admin_cuit ? formatCuitToString(persona.admin_cuit) : null,
      admin_nombre: persona?.admin_nombre || null,
      servicios: serviciosSeleccionados,
      // Los servicios leídos van en los defaults. Si la ficha cambia con el formulario sin tocar,
      // TanStack reemplaza los valores por los defaults: sin los servicios, la lista quedaría vacía
      // contra una línea base con servicios, y el guardado los daría de baja.
      serviciosConPrecio: serviciosLeidos ? serviciosDelFormulario(serviciosLeidos) : [],
      etiquetas: etiquetasSeleccionadas,
    }
  }, [cliente, serviciosSeleccionados, etiquetasSeleccionadas, serviciosLeidos, valoresIniciales])

  // Filas de servicios que el formulario precargó: la línea base del guardado. Null hasta la primera carga.
  const serviciosBase = useRef<ServicioLeido[] | null>(null)

  // Inicializar el formulario con TanStack Form
  const {
    form,
    currentStep,
    stepErrors,
    nextStep,
    prevStep,
    goToStep,
    validateStep,
    isFirstStep,
    isLastStep,
  } = useClienteForm({
    defaultValues,
    onSubmit: (values) => onSubmit(values, { serviciosBase: serviciosBase.current }),
    stepSchemas: mostrarPasoDeuda ? STEP_SCHEMAS_CON_DEUDA : STEP_SCHEMAS,
  })

  // Sincronizar servicios del cliente cuando se cargan (modo edición).
  // Con el formulario limpio, cada lectura lo reemplaza y pasa a ser la línea base del guardado. Si ya
  // está tocado, solo entra la primera: se carga sin limpiarlo y se suma a lo que el usuario agregó. Las
  // lecturas siguientes no lo pisan ni cambian la línea base; el guardado aplica la diferencia contra lo
  // que el usuario vio y no toca lo que otro agregó mientras tanto.
  useEffect(() => {
    if (!isEditMode || !serviciosLeidos) return
    const serviciosFormateados = serviciosDelFormulario(serviciosLeidos)
    if (!form.state.isDirty) {
      form.reset(
        { ...form.state.values, serviciosConPrecio: serviciosFormateados },
        { keepDefaultValues: true },
      )
      serviciosBase.current = serviciosLeidos
    } else if (serviciosBase.current === null) {
      const agregados = (form.state.values.serviciosConPrecio ?? []).filter(
        (agregado) => !serviciosFormateados.some((s) => s.servicio_id === agregado.servicio_id),
      )
      form.setFieldValue('serviciosConPrecio', [...serviciosFormateados, ...agregados])
      serviciosBase.current = serviciosLeidos
    }
  }, [isEditMode, serviciosLeidos, form])

  const isDirty = useStore(form.store, (s) => s.isDirty)
  useEffect(() => {
    onDirtyChange?.(isDirty)
    return () => onDirtyChange?.(false)
  }, [isDirty, onDirtyChange])

  // Track de pasos completados para UI
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => {
    // En modo edición, todos los pasos se consideran completos inicialmente
    if (isEditMode) {
      return new Set([0, 1, 2, 3])
    }
    return new Set()
  })

  // Estado para mostrar/ocultar panel de errores
  const [showErrorPanel, setShowErrorPanel] = useState(true)

  // Marcar paso como completo al avanzar
  const handleNextStep = useCallback(async () => {
    const success = await nextStep()
    if (success) {
      setCompletedSteps((prev) => new Set([...prev, currentStep]))
    } else {
      // Mostrar panel de errores si hay errores
      setShowErrorPanel(true)
    }
    return success
  }, [nextStep, currentStep])

  // Manejar click en stepper
  const handleStepClick = useCallback(
    async (stepIndex: number) => {
      await goToStep(stepIndex, isEditMode)
    },
    [goToStep, isEditMode]
  )

  // Verificar si puede ir a un paso
  const canGoToStep = useCallback(
    (step: number): boolean => {
      // Siempre puede ir hacia atrás
      if (step < currentStep) return true
      // En modo edición puede ir a cualquier paso
      if (isEditMode) return true
      // Solo puede ir a pasos ya completados o al siguiente
      return completedSteps.has(step) || step === currentStep
    },
    [currentStep, completedSteps, isEditMode]
  )

  // Manejar submit final
  const handleSubmit = useCallback(async () => {
    if (isSaving) return
    // Validar el paso actual antes de enviar
    const isValid = await validateStep(currentStep)
    if (!isValid) {
      setShowErrorPanel(true)
      return
    }
    form.handleSubmit()
  }, [form, validateStep, currentStep, isSaving])

  // Renderizar el paso actual
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <StepIdentidadNew form={form} clienteId={cliente?.id} />
      case 1:
        return <StepDomicilioNew form={form} />
      case 2:
        return <StepFiscalNew form={form} />
      case 3:
        return (
          <StepServiciosNew
            form={form}
            serviciosDisponibles={serviciosDisponibles}
            usuarios={usuarios}
            mostrarEtiquetas={mostrarEtiquetas}
          />
        )
      case 4:
        return <StepDeudaInicialNew form={form} servicios={serviciosData} />
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Stepper */}
      <div className="shrink-0 pb-4">
        <WizardStepper
          steps={wizardSteps}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
          canGoToStep={canGoToStep}
        />
      </div>

      {/* Contenido del paso */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-1 pr-4">
          {renderCurrentStep()}
        </div>
      </ScrollArea>

      {/* Errores de validación */}
      {showErrorPanel && stepErrors[currentStep] && stepErrors[currentStep].length > 0 && (
        <div className="shrink-0 mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-1 right-1 h-6 w-6 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
            onClick={() => setShowErrorPanel(false)}
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="flex items-start gap-2 text-destructive pr-6">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-medium text-sm">Por favor completa los siguientes campos:</p>
              <ul className="text-sm list-disc list-inside space-y-0.5">
                {stepErrors[currentStep].map((error: string, idx: number) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Navegación */}
      <div className="shrink-0 mt-4">
        <WizardNavigation
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          isLoading={isSaving}
          onPrev={prevStep}
          onNext={handleNextStep}
          onSubmit={handleSubmit}
          submitLabel={isEditMode ? 'Guardar Cambios' : 'Crear Cliente'}
        />
      </div>
    </div>
  )
}
